/**
 * Forge CRUD operations for Spaces, Members, and Invitations.
 */
import { forge, auth } from './ezhard';
import { where } from '@ezhard/sdk';
import type { QueryConstraint } from '@ezhard/sdk';
import type { Space, Invitation, User } from '@/types/models';
import type { Role } from '@/types/roles';
import { logEvent } from './analytics';

/** Maximum number of spaces a free-tier user can participate in. */
const FREE_TIER_SPACE_LIMIT = 3;

/* ─── Reference helpers ─── */

export function getSpacePath(spaceId: string) {
  return `spaces/${spaceId}`;
}

export function getMembersPath(spaceId: string) {
  return `spaces/${spaceId}/members`;
}

export function getInvitationsPath(spaceId: string) {
  return `spaces/${spaceId}/invitations`;
}

/**
 * Builds Forge query constraints for spaces whose IDs are in the given array.
 */
export function getUserSpacesConstraints(spaceIds: string[]): QueryConstraint[] {
  if (spaceIds.length === 0) return [];
  return [where('id', 'in', spaceIds.slice(0, 30))];
}

/* ─── Mutations ─── */

/** Ensures the current user is authenticated, throwing if not. */
function requireAuth() {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to perform this action.');
  return user;
}

/**
 * Creates a new space and adds the creator as the owner member.
 * Also adds the spaceId to the user's spaceIds array.
 * @returns The new space's document ID.
 */
export async function createSpace(
  name: string,
  description: string,
): Promise<string> {
  const user = requireAuth();

  // Client-side tier check
  const userSnap = await forge.doc(`users/${user.id}`).get();
  if (userSnap.exists && (userSnap.data() as unknown as User).subscriptionTier !== 'power') {
    throw new Error('Creating spaces requires a Power account.');
  }

  // Create space document
  const spaceId = await forge.collection('spaces').add({
    name,
    description,
    ownerId: user.id,
    memberCount: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Add creator as owner member
  await forge.doc(`spaces/${spaceId}/members/${user.id}`).set({
    role: 'owner' as Role,
    displayName: user.displayName ?? '',
    email: user.email ?? '',
    invitedBy: user.id,
    joinedAt: new Date().toISOString(),
  });

  // Add spaceId to user's document
  const currentSpaceIds = (userSnap.data() as unknown as User)?.spaceIds || [];
  if (!currentSpaceIds.includes(spaceId)) {
    await forge.doc(`users/${user.id}`).update({
      spaceIds: [...currentSpaceIds, spaceId],
      updatedAt: new Date().toISOString(),
    });
  }

  logEvent('create_space', { spaceId });

  return spaceId;
}

/**
 * Updates a space's name and/or description.
 */
export async function updateSpace(
  spaceId: string,
  data: Partial<Pick<Space, 'name' | 'description'>>,
): Promise<void> {
  await forge.doc(`spaces/${spaceId}`).update({
    ...data,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Deletes a space and removes it from the current user's spaceIds.
 */
export async function deleteSpace(spaceId: string): Promise<void> {
  const user = requireAuth();

  await forge.doc(`spaces/${spaceId}`).delete();

  const userSnap = await forge.doc(`users/${user.id}`).get();
  if (userSnap.exists) {
    const currentSpaceIds = (userSnap.data() as unknown as User).spaceIds || [];
    await forge.doc(`users/${user.id}`).update({
      spaceIds: currentSpaceIds.filter((id) => id !== spaceId),
      updatedAt: new Date().toISOString(),
    });
  }
  
  logEvent('delete_space', { spaceId });
}

/**
 * Creates a pending invitation for a new member.
 * @returns The invitation document ID.
 */
export async function inviteMember(
  spaceId: string,
  email: string,
  role: Role,
): Promise<string> {
  const user = requireAuth();

  const id = await forge.collection(`spaces/${spaceId}/invitations`).add({
    email,
    role,
    invitedBy: user.id,
    invitedByName: user.displayName ?? '',
    status: 'pending',
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  });

  logEvent('invite_member', { spaceId, role });

  return id;
}

/**
 * Accepts an invitation: updates status, creates a member doc,
 * adds spaceId to user, and increments memberCount.
 */
export async function acceptInvitation(
  spaceId: string,
  invitationId: string,
): Promise<void> {
  const user = requireAuth();

  // Client-side tier check
  const userSnap = await forge.doc(`users/${user.id}`).get();
  if (userSnap.exists) {
    const userData = userSnap.data() as unknown as User;
    if (
      userData.subscriptionTier !== 'power' &&
      (userData.spaceIds?.length ?? 0) >= FREE_TIER_SPACE_LIMIT
    ) {
      throw new Error('Free accounts can join up to 3 spaces.');
    }
  }

  // Fetch invitation data
  const invSnap = await forge.doc(`spaces/${spaceId}/invitations/${invitationId}`).get();
  if (!invSnap.exists) throw new Error('Invitation not found.');

  const invData = invSnap.data() as unknown as Invitation;

  // Update invitation status
  await forge.doc(`spaces/${spaceId}/invitations/${invitationId}`).update({ status: 'accepted' });

  // Create member doc
  await forge.doc(`spaces/${spaceId}/members/${user.id}`).set({
    role: invData.role,
    displayName: user.displayName ?? '',
    email: user.email ?? '',
    invitedBy: invData.invitedBy,
    joinedAt: new Date().toISOString(),
  });

  // Add spaceId to user's spaceIds
  if (userSnap.exists) {
    const currentSpaceIds = (userSnap.data() as unknown as User).spaceIds || [];
    if (!currentSpaceIds.includes(spaceId)) {
      await forge.doc(`users/${user.id}`).update({
        spaceIds: [...currentSpaceIds, spaceId],
        updatedAt: new Date().toISOString(),
      });
    }
  }

  // Increment space member count
  const spaceSnap = await forge.doc(`spaces/${spaceId}`).get();
  if (spaceSnap.exists) {
    const currentCount = (spaceSnap.data() as unknown as Space).memberCount || 0;
    await forge.doc(`spaces/${spaceId}`).update({
      memberCount: currentCount + 1,
      updatedAt: new Date().toISOString(),
    });
  }
  
  logEvent('accept_invitation', { spaceId });
}

/**
 * Removes a member from a space.
 */
export async function removeMember(
  spaceId: string,
  userId: string,
): Promise<void> {
  // Delete member doc
  await forge.doc(`spaces/${spaceId}/members/${userId}`).delete();

  // Remove spaceId from user
  const userSnap = await forge.doc(`users/${userId}`).get();
  if (userSnap.exists) {
    const currentSpaceIds = (userSnap.data() as unknown as User).spaceIds || [];
    await forge.doc(`users/${userId}`).update({
      spaceIds: currentSpaceIds.filter((id) => id !== spaceId),
      updatedAt: new Date().toISOString(),
    });
  }

  // Decrement space member count
  const spaceSnap = await forge.doc(`spaces/${spaceId}`).get();
  if (spaceSnap.exists) {
    const currentCount = (spaceSnap.data() as unknown as Space).memberCount || 0;
    await forge.doc(`spaces/${spaceId}`).update({
      memberCount: Math.max(0, currentCount - 1),
      updatedAt: new Date().toISOString(),
    });
  }
  
  logEvent('remove_member', { spaceId });
}

/**
 * Changes a member's role within a space.
 */
export async function changeMemberRole(
  spaceId: string,
  userId: string,
  newRole: Role,
): Promise<void> {
  await forge.doc(`spaces/${spaceId}/members/${userId}`).update({
    role: newRole,
  });
  
  logEvent('change_member_role', { spaceId, newRole });
}
